import { cloneDeep, isEqual } from 'lodash';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { FEED_BATCH_SIZE } from '../../Base.constants';
import {
  ContentTypeTag,
  COVENANT_TYPE_TAG_LIST,
  CREDIT_GRADE_SPECIAL_SITUATIONS_TAG,
  FeedType,
  Filters,
  FilterType,
  NEWS_TYPE_TAG_LIST,
} from '../../models';
import { ArticleTableRow } from '../../models/ArticleTableRow';
import { FetchArticlesFilterParams } from '../../services/articles/articles.models';
import { fetchArticles, fetchMyFeedArticles } from '../../services/articles/articles.service';
import { ArticleListDto } from '../../services/dtos/article/articleList.dto';
import { getGroupedFilterTags, getTitleContainsFilter } from '../../utils/filterUtils';
import { queryKeys } from '../queryKeyFactory';
import { mapArticleListItemDtoToArticleTableRow } from './utils/article.mappers';

interface PaginatedArticleResults {
  data: ArticleTableRow[] | undefined;
  isFetching: boolean;
  totalPages: number;
  isError: boolean;
  refetch: () => void;
  totalResults?: number;
}

const globalArticlesQueryFn =
  (page = 0, take = FEED_BATCH_SIZE) =>
  ({ meta }) =>
    fetchArticles({ page, take, filters: meta.filters });

const myFeedArticlesQueryFn =
  (page = 0, take = FEED_BATCH_SIZE) =>
  ({ meta }) =>
    fetchMyFeedArticles({ page, take, filters: meta.filters });

const ArticleQueryFn: Record<FeedType, (page, take) => ({ meta }) => Promise<ArticleListDto>> = {
  [FeedType.My]: myFeedArticlesQueryFn,
  [FeedType.Global]: globalArticlesQueryFn,
  [FeedType.SpecialSituations]: globalArticlesQueryFn,
};

export default (
  feedType: FeedType,
  take: number,
  page: number,
  filters: Filters | null,
  contentTypeFilters: string[],
  issuerTag: string | undefined,
  enabled: boolean = true
): PaginatedArticleResults => {
  const queryFn = ArticleQueryFn[feedType];

  const filterParams: FetchArticlesFilterParams | null = useMemo(() => {
    if (!filters) {
      return null;
    }

    const copy = cloneDeep(filters);
    copy.tags = copy.tags || {};

    const isCovenantTypeFilterApplied = isEqual(contentTypeFilters, [ContentTypeTag.Covenants]);
    const isNewsTypeFilterApplied = isEqual(contentTypeFilters, [ContentTypeTag.News]);

    if (enabled) {
      if (isCovenantTypeFilterApplied) {
        const covenantFilters =
          copy.tags[FilterType.covenants_source]?.filter(
            (tag) => tag in COVENANT_TYPE_TAG_LIST || tag === ContentTypeTag.Covenants
          ) || [];

        const isCovenantSubTypeFilterApplied = isCovenantTypeFilterApplied && covenantFilters.length > 0;

        if (isCovenantSubTypeFilterApplied) {
          copy.tags[FilterType.covenants_source] = covenantFilters;
        }

        copy.tags.ContentTypeTag = contentTypeFilters;
      } else if (isNewsTypeFilterApplied) {
        const newsFilters =
          copy.tags[FilterType.news_source]?.filter(
            (tag) => tag in NEWS_TYPE_TAG_LIST || tag === ContentTypeTag.News
          ) || [];
        const isNewsSubTypeFilterApplied = isNewsTypeFilterApplied && newsFilters.length > 0;
        if (isNewsSubTypeFilterApplied) {
          copy.tags[FilterType.news_source] = newsFilters;
        }
        copy.tags.ContentTypeTag = contentTypeFilters;
      } else if (contentTypeFilters.length > 0) {
        copy.tags[FilterType.source] = contentTypeFilters;
      }
    } else {
      delete copy.tags[FilterType.source];
      delete copy.tags[FilterType.covenants_source];
      delete copy.tags[FilterType.news_source];
    }
    if (feedType === FeedType.SpecialSituations) {
      copy.tags[FilterType.creditGrade] = [CREDIT_GRADE_SPECIAL_SITUATIONS_TAG];
    }

    return {
      startDate: copy?.startDate,
      endDate: copy?.endDate,
      textContains: isCovenantTypeFilterApplied ? getTitleContainsFilter(copy?.titleContains) : undefined,
      tags: getGroupedFilterTags(copy),
      focusTagId: issuerTag,
    };
  }, [filters, contentTypeFilters, issuerTag]);

  const query = useQuery(
    queryKeys.fetchPaginatedArticles.paginatedArticles(feedType, filterParams, page, take),
    queryFn(page - 1, take),
    {
      enabled: enabled && filterParams !== null,
      meta: { filters: filterParams },
      select: (articles) => {
        return {
          results: mapArticleListItemDtoToArticleTableRow(articles.results),
          totalPages: Math.ceil(articles.totalResults / take),
          totalResults: articles.totalResults,
        };
      },
    }
  );

  return {
    data: query.data?.results,
    totalPages: query.data?.totalPages || 0,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
    totalResults: query.data?.totalResults,
  };
};
